// src/pages/Auth/RegisterPersonnel.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, writeBatch } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../firebase';

const styles = {
    container: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f4f6f9' },
    formBox: { padding: '40px', background: 'white', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', width: '400px', textAlign: 'center' },
    input: { width: '100%', padding: '12px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', marginBottom: '15px' },
    button: { width: '100%', padding: '12px', border: 'none', background: '#27ae60', color: 'white', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' },
    error: { color: 'red', marginBottom: '15px' },
    info: { color: '#555', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '15px' }
};

function RegisterPersonnel() {
    const { companyId, invitationId } = useParams();
    const navigate = useNavigate();

    const [invitation, setInvitation] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [isValid, setIsValid] = useState(false);

    // Firma ID'si için yeni state
    const [companyName, setCompanyName] = useState('');
    // Personel e-posta adresi için state (davetiye yoluyla değilse)
    const [personnelEmail, setPersonnelEmail] = useState('');

    const fetchInvitation = useCallback(async () => {
        try {
            // Eğer davetiye ID'si 'direct' ise doğrudan firma ID'si ile kayıt
            if (invitationId === 'direct') {
                setIsValid(true);
                setLoading(false);
                return;
            }

            const invRef = doc(db, `companies/${companyId}/invitations/${invitationId}`);
            const docSnap = await getDoc(invRef);

            if (docSnap.exists() && docSnap.data().status === 'pending') {
                setInvitation(docSnap.data());
                setIsValid(true);
                setPersonnelEmail(docSnap.data().email); // E-posta adresini ayarla
            } else {
                setError('Bu davetiye geçerli değil veya daha önce kullanılmış.');
            }
        } catch (err) {
            setError('Davetiye bilgileri alınırken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [companyId, invitationId]);

    useEffect(() => {
        fetchInvitation();
    }, [fetchInvitation]);

    const handleRegister = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Şifreler eşleşmiyor.');
            return;
        }
        if (password.length < 6) {
            setError('Şifre en az 6 karakter olmalıdır.');
            return;
        }
        setError('');
        setLoading(true);

        try {
            // 1. Firebase Auth ile yeni kullanıcı oluştur
            const userCredential = await createUserWithEmailAndPassword(auth,
                invitationId === 'direct' ? personnelEmail : invitation.email, password);
            const newUser = userCredential.user;

            // 2. İşlemleri toplu halde yapmak için batch oluştur
            const batch = writeBatch(db);

            const userRole = invitation?.role || 'personel';
            const customerId = invitation?.customerId || null;

            // 3. 'personnel' koleksiyonuna yeni personeli ekle (Sadece personel ise)
            if (userRole === 'personel') {
                const personnelRef = doc(db, `companies/${companyId}/personnel`, newUser.uid);
                batch.set(personnelRef, {
                    email: invitationId === 'direct' ? personnelEmail : invitation.email,
                    role: 'personel',
                    permissions: invitationId === 'direct' ? {
                        // Ana menü yetkileri
                        dashboard: true,
                        raporlar: false,
                        kasa: false,
                        musteriler: false,
                        tedarikciler: false,
                        stok: false,
                        alisFaturalari: false,
                        satisFaturalari: false,
                        siparisler: false,
                        istekSiparisler: false,
                        ayarlar: false,
                        viewPrices: false,

                        // Detaylı yetkiler - Müşteriler
                        viewCustomers: false,
                        createCustomers: false,
                        editCustomers: false,
                        deleteCustomers: false,

                        // Detaylı yetkiler - Siparişler
                        viewOrders: false,
                        createOrders: false,
                        editOrders: false,
                        deleteOrders: false,

                        // Detaylı yetkiler - Stok
                        viewStock: false,
                        addStock: false,
                        editStock: false,
                        deleteStock: false,

                        // Detaylı yetkiler - Kasa & Banka
                        viewCashBank: false,
                        addCashBank: false,
                        editCashBank: false,

                        // Detaylı yetkiler - Faturalar
                        viewInvoices: false,
                        createInvoices: false,
                        editInvoices: false,
                        deleteInvoices: false
                    } : invitation.permissions
                });
            }

            // 4. Ana 'users' koleksiyonuna yeni kullanıcıyı ekle
            const userRef = doc(db, 'users', newUser.uid);
            batch.set(userRef, {
                email: invitationId === 'direct' ? personnelEmail : invitation.email,
                companyId: companyId,
                role: userRole,
                customerId: customerId,
                companyName: companyName || invitation?.companyName || 'Belirtilmemiş',
                displayName: invitation?.customerName || '',
                phone: '',
                emailNotifications: true,
                pushNotifications: true
            });

            // 5. Eğer davetiye yoluyla kayıt değilse, davetiye güncelleme adımını atla
            if (invitationId !== 'direct') {
                const invRef = doc(db, `companies/${companyId}/invitations/${invitationId}`);
                batch.update(invRef, { status: 'completed' });
            }

            // 6. Tüm işlemleri tek seferde gerçekleştir
            await batch.commit();

            navigate('/login?status=registered');

        } catch (err) {
            console.error(err);
            if (err.code === 'auth/email-already-in-use') {
                setError('Bu e-posta adresi ile zaten bir hesap oluşturulmuş.');
            } else {
                setError('Kayıt sırasında bir hata oluştu.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={styles.container}><p>Yükleniyor...</p></div>;

    return (
        <div style={styles.container}>
            <div style={styles.formBox}>
                <h2>Hesabınızı Oluşturun</h2>
                {!isValid ? (
                    <p style={styles.error}>{error}</p>
                ) : (
                    <form onSubmit={handleRegister}>
                        <p style={styles.info}>
                            {invitationId === 'direct' ? (
                                <span><b>{companyId}</b> firmasına personel olarak kayıt oluyorsunuz.</span>
                            ) : (
                                <span><b>{invitation.email}</b> e-posta adresi ile şirkete davet edildiniz.</span>
                            )}
                        </p>
                        {invitationId === 'direct' && (
                            <div>
                                <input
                                    style={styles.input}
                                    type="email"
                                    value={personnelEmail}
                                    onChange={(e) => setPersonnelEmail(e.target.value)}
                                    placeholder="Personel E-posta Adresi"
                                    required
                                />
                                <input
                                    style={styles.input}
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    placeholder="Firma Adı (Opsiyonel)"
                                />
                            </div>
                        )}
                        <input
                            style={styles.input}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Yeni Şifreniz"
                            required
                        />
                        <input
                            style={styles.input}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Şifrenizi Onaylayın"
                            required
                        />
                        {error && <p style={styles.error}>{error}</p>}
                        <button type="submit" style={styles.button} disabled={loading}>
                            {loading ? 'İşleniyor...' : 'Hesabı Oluştur ve Giriş Yap'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

export default RegisterPersonnel;
